import { FC, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import Skeleton from '@ingka/skeleton';

import {
  CodeV2 as Code,
  useGetV1StatsBySlugScansQuery,
} from '@/redux/apis/codes-api';
import { SquareDot } from '@/components/data/recharts/dot';
import { ScansChartTooltip } from '@/components/data/recharts/tooltip';
import { getDaysSinceDate } from '@/lib/utils/date';
import { useChartContext } from './context';
import { Granularity } from './constants';
import { ChartPoint, groupByDay, groupByHour } from './reducers';

const GROUP_REDUCERS: Record<
  Granularity,
  (from: number, to: number, data: Record<string, string[]>) => ChartPoint[]
> = {
  hour: groupByHour,
  day: groupByDay,
};

const TOOLTIP_BY_GRANULARITY: Record<Granularity, string> = {
  day: 'LLL dd',
  hour: 'LLL dd, HH:mm',
};

const TICK_BY_GRANULARITY: Record<Granularity, string> = {
  day: 'LLL dd',
  hour: 'LLL dd HH:mm',
};

const CHART_HEIGHT = 400;

export const QrCodeChartRenderer: FC<{
  scanData: Record<string, string[]>;
  linkData: Record<string, string[]>;
}> = ({ scanData, linkData }) => {
  const { date, granularity } = useChartContext();
  const chartData = useMemo(() => {
    const groupedScanData = GROUP_REDUCERS[granularity](
      date.from,
      date.to,
      scanData
    );
    const groupedLinkData = GROUP_REDUCERS[granularity](
      date.from,
      date.to,
      linkData
    );

    return groupedScanData.map(({ time, value }, index) => ({
      time,
      scanData: value,
      linkData: groupedLinkData[index]?.value ?? 0,
    }));
  }, [granularity, scanData, linkData, date]);
  const maxValue = Math.max(
    ...chartData.map((d) => Math.max(d.scanData, d.linkData)),
    0
  );
  const paddingLeft = `${maxValue}`.length * 5 - 5;
  const tooltipFormat = TOOLTIP_BY_GRANULARITY[granularity];
  const tickFormat = TICK_BY_GRANULARITY[granularity];
  const showDots = chartData.length <= 31;

  return (
    <div style={{ height: CHART_HEIGHT }}>
      <ResponsiveContainer>
        <LineChart
          data={chartData}
          margin={{ top: 8, left: paddingLeft, bottom: 14, right: 5 }}
        >
          <CartesianGrid strokeDasharray="0" vertical={false} strokeWidth={1} />
          <XAxis
            interval={'preserveEnd'}
            axisLine={false}
            tickLine={false}
            tickMargin={24}
            tickFormatter={(time: number) =>
              DateTime.fromMillis(time).toFormat(tickFormat)
            }
            dataKey="time"
            stroke="rgb(var(--colour-text-and-icon-1)"
          />
          <YAxis
            interval="preserveStartEnd"
            axisLine={false}
            tickLine={false}
            tickMargin={24}
            stroke="rgb(var(--colour-text-and-icon-1)"
          />
          <Line
            type="linear"
            dataKey="linkData"
            name="Direct links"
            stroke="rgb(var(--colour-semantic-caution))"
            strokeWidth={3}
            legendType="square"
            dot={
              showDots && (
                <SquareDot color="rgb(var(--colour-semantic-caution))" />
              )
            }
            activeDot={
              <SquareDot color="rgb(var(--colour-semantic-caution))" />
            }
          />
          <Line
            type="linear"
            dataKey="scanData"
            name="Scans"
            stroke="rgb(var(--colour-interactive-emphasised-border-selected))"
            strokeWidth={3}
            legendType="square"
            dot={
              showDots && (
                <SquareDot color="rgb(var(--colour-interactive-emphasised-border-selected))" />
              )
            }
            activeDot={
              <SquareDot color="rgb(var(--colour-interactive-emphasised-border-selected))" />
            }
          />
          <Tooltip content={<ScansChartTooltip format={tooltipFormat} />} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const QrCodeChart: FC<{ code: Code }> = ({ code }) => {
  const { date } = useChartContext();
  const dayDiff = getDaysSinceDate(new Date(date.from));
  const days = dayDiff <= 30 ? 30 : dayDiff;
  const {
    data: scanData,
    isLoading: isLoadingScan,
    isFetching: isFetchingScan,
  } = useGetV1StatsBySlugScansQuery(
    {
      slug: code.slug!,
      days,
      scan: 'true',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: linkData,
    isLoading: isLoadingLink,
    isFetching: isFetchingLink,
  } = useGetV1StatsBySlugScansQuery(
    {
      slug: code.slug!,
      days,
      scan: 'false',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const loading =
    isLoadingScan || isLoadingLink || isFetchingScan || isFetchingLink;

  if (loading || !scanData || !linkData) {
    return (
      <Skeleton
        data-testid="loading"
        height={`${CHART_HEIGHT}`}
        style={{ height: CHART_HEIGHT }}
      />
    );
  }

  return <QrCodeChartRenderer scanData={scanData} linkData={linkData} />;
};
